import axios from "axios";
import { isTokenExpired, clearSessionAndRedirect } from "./utils/tokenValidator";

// Create an Axios instance
const http = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include the Bearer token
http.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if ((host === 'localhost' || host === '127.0.0.1') && typeof sessionStorage !== 'undefined') {
        const tenant = sessionStorage.getItem('__typogram_tenant');
        if (tenant) {
          config.headers["X-Tenant-Host"] = tenant + ".typogram.in";
        } else {
          config.headers["X-Tenant-Host"] = host;
        }
      } else {
        config.headers["X-Tenant-Host"] = host;
      }
    }
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      return config;
    }

    try {
      const user = JSON.parse(userStr);

    // If the user is logged in and has an accessToken, add it to the headers
    if (user && user.accessToken) {
        // Check if token is expired before making request
        if (isTokenExpired(user.accessToken)) {
          // Token expired - clear session and redirect
          clearSessionAndRedirect();
          return Promise.reject(new Error('Token expired'));
        }
      config.headers.Authorization = `Bearer ${user.accessToken}`;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      localStorage.removeItem("user");
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration or unauthorized access
http.interceptors.response.use(
  (response) => {
    // Do not parse Blob bodies (e.g. audio/pdf) as JSON — breaks voice preview & file downloads.
    const isBlob = typeof Blob !== "undefined" && response.data instanceof Blob;
    if (!isBlob && typeof response.data === "string") {
      try {
        response.data = JSON.parse(response.data);
      } catch (e) {
        console.warn("Failed to parse response as JSON:", e);
      }
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        // No user in storage — could be a demo/guest session.
        // Don't redirect; let the calling code handle the error.
        return Promise.reject(error);
      }

      try {
        const user = JSON.parse(userStr);
        const requestUrl = error.config?.url || '';
        
        const authEndpoints = ['/auth/signin', '/auth/signup', '/auth/google'];
        const isAuthEndpoint = authEndpoints.some(ep => requestUrl.includes(ep));
      
        if (user && !isAuthEndpoint) {
          clearSessionAndRedirect();
        }
      } catch (parseError) {
        console.error('Error parsing user from localStorage:', parseError);
        localStorage.removeItem("user");
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default http;